import {
  Component,
  OnInit,
  inject,
  signal,
  computed,
  effect,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { forkJoin } from 'rxjs';

import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule } from 'ng-zorro-antd/modal'; // Added modal module
import { NzButtonModule } from 'ng-zorro-antd/button'; // Added button module
import { NzTooltipModule } from 'ng-zorro-antd/tooltip'; // Added tooltip module
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';

import { AuthStore } from '@rapider/authentication';
import { ProjectModelControllerService } from '@rapider/sdk';
import { NotificationService } from '@rapider/services';
import {
  ProjectDataActions,
  ProjectDataService,
  selectProjectModelsList,
} from '@rapider/project-data';

import {
  DATABASE_CONFIG,
  buildProjectModelsFilter,
  groupAndSortModels,
  toggleRecordState,
  setAllRecordStates,
  extractDisplayedModelIds, // Added pure function
  ProjectModelGroup,
  DatabaseAccent,
  getDatabaseAccent,
} from '../database.config';

/**
 * @class DatabaseListComponent
 * @description Renders a filtered, searchable, and grouped list of database models for the active project.
 */
@Component({
  selector: 'rapider-database-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzTagModule,
    NzInputModule,
    NzModalModule,
    NzButtonModule,
    NzTooltipModule,
    NzPopconfirmModule,
    NzDropDownModule,
  ],
  templateUrl: './database-list.component.html',
  host: {
    class:
      'block h-full w-full bg-body-background overflow-y-auto custom-scrollbar',
  },
})
export class DatabaseListComponent implements OnInit {
  private readonly projectModelController = inject(
    ProjectModelControllerService,
  );
  private readonly notificationService = inject(NotificationService);
  private readonly authStore = inject(AuthStore);
  private readonly router = inject(Router);
  private readonly store = inject(Store); // Added Store injection
  private readonly projectDataService = inject(ProjectDataService);

  public readonly config = DATABASE_CONFIG;

  // Active Data Signal initialized from cached store for instant SWR rendering
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public models = signal<any[]>(
    this.store.selectSignal(selectProjectModelsList)() || [],
  );

  // Base Computed Signal resolving grouped tables ready for presentation
  private groupedModels = computed(() => groupAndSortModels(this.models()));

  // Interaction UI State: only show loading if no cached models exist
  public isLoading = signal<boolean>(this.models().length === 0);
  public searchQuery = signal<string>('');
  public selectedDomain = signal<string | null>(null);
  public expandedGroups = signal<Record<string, boolean>>({});

  // AI Context Modal Signals
  public isAiContextModalVisible = signal<boolean>(false);
  public isAiContextLoading = signal<boolean>(false);
  public aiContextContent = signal<string>('');
  public isCopied = signal<boolean>(false);

  // Interactions & Access State
  public readonly isFreeTier = computed<boolean>(() => {
    const project = this.authStore.activeProject();
    return !project?.['tier'] || project['tier'] === 'free';
  });

  // Cascade Deletion Approval Modal State
  public isDeleteModalVisible = signal<boolean>(false);
  public deleteTargetType = signal<'model' | 'group'>('model');
  public modelToDelete = signal<any | null>(null);
  public groupToDelete = signal<ProjectModelGroup | null>(null);
  public deleteConfirmationInput = signal<string>('');
  public isDeleting = signal<boolean>(false);

  // Expected confirmation string (exact model name/title or domain key)
  public expectedConfirmationText = computed<string>(() => {
    if (this.deleteTargetType() === 'model') {
      const model = this.modelToDelete();
      return model?.title || model?.name || '';
    } else {
      const group = this.groupToDelete();
      return group?.domainKey || '';
    }
  });

  // Approved only when user enters the required model name or domain key
  public isDeleteConfirmed = computed<boolean>(() => {
    const input = this.deleteConfirmationInput().trim().toLowerCase();
    if (!input) return false;

    if (this.deleteTargetType() === 'model') {
      const model = this.modelToDelete();
      const expectedName = (model?.name || '').trim().toLowerCase();
      const expectedTitle = (model?.title || '').trim().toLowerCase();
      return (
        (expectedName.length > 0 && input === expectedName) ||
        (expectedTitle.length > 0 && input === expectedTitle)
      );
    } else {
      const group = this.groupToDelete();
      const expectedKey = (group?.domainKey || '').trim().toLowerCase();
      const expectedName = (group?.domainName || '').trim().toLowerCase();
      return (
        (expectedKey.length > 0 && input === expectedKey) ||
        (expectedName.length > 0 && input === expectedName)
      );
    }
  });

  // Derived Computed Signal extracting unique domains for the filter tags
  public uniqueDomains = computed(() => {
    return this.groupedModels().map((g: ProjectModelGroup) => ({
      key: g.domainKey,
      name: g.domainName,
    }));
  });

  // Derived Computed Signal applying search and domain filters to the groups
  public filteredGroupedModels = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const domain = this.selectedDomain();
    let groups: ProjectModelGroup[] = this.groupedModels();

    if (domain) {
      groups = groups.filter((g: ProjectModelGroup) => g.domainKey === domain);
    }

    if (query) {
      groups = groups
        .map((g: ProjectModelGroup) => ({
          ...g,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          models: g.models.filter(
            (m: any) =>
              (m.title && m.title.toLowerCase().includes(query)) ||
              (m.dataTableName &&
                m.dataTableName.toLowerCase().includes(query)) ||
              (m.description && m.description.toLowerCase().includes(query)),
          ),
        }))
        .filter((g: ProjectModelGroup) => g.models.length > 0);
    }

    return groups;
  });

  // Computed Signal to determine if all visible groups are expanded
  public areAllGroupsExpanded = computed(() => {
    const groups = this.filteredGroupedModels();
    if (groups.length === 0) return true;
    return groups.every(
      (g: ProjectModelGroup) => this.expandedGroups()[g.domainKey] !== false,
    );
  });

  /** Total number of tables (models) in the workspace. */
  public readonly totalTableCount = computed(() => this.models().length);

  /** Number of tables currently visible after filters. */
  public readonly visibleTableCount = computed(() =>
    this.filteredGroupedModels().reduce(
      (sum: number, g: ProjectModelGroup) => sum + g.models.length,
      0,
    ),
  );

  /** Table counts per domain key (unfiltered), used on the database pills. */
  public readonly domainTableCounts = computed(() => {
    const counts: Record<string, number> = {};
    this.groupedModels().forEach((g: ProjectModelGroup) => {
      counts[g.domainKey] = g.models.length;
    });
    return counts;
  });

  /** Alias for domainTableCounts used in template. */
  public readonly domainCounts = this.domainTableCounts;

  /**
   * Returns the stable accent palette for a domain rendered as a database.
   * @param {string} domainKey - Domain key.
   * @returns {DatabaseAccent} Accent classes.
   */
  public getAccent(domainKey: string): DatabaseAccent {
    return getDatabaseAccent(domainKey);
  }

  constructor() {
    // Keep models synchronized with NgRx store cache
    effect(() => {
      const cached = this.store.selectSignal(selectProjectModelsList)();
      if (cached && cached.length > 0 && this.models().length === 0) {
        this.models.set(cached);
        this.isLoading.set(false);
      } else if ((!cached || cached.length === 0) && !this.authStore.activeProject()?.id) {
        this.models.set([]);
      }
    }, { allowSignalWrites: true });

    /**
     * EFFECT: Reactively reload data if the active project changes (e.g., on page refresh).
     * This solves the issue where AuthStore might not be populated instantly on load.
     */
    effect(() => {
      const activeProject = this.authStore.activeProject();
      const projectId = activeProject?.id;

      // Execute the state update outside the reactive context to prevent infinite effect loops
      untracked(() => {
        if (projectId) {
          this.fetchModels(projectId);
        } else {
          this.models.set([]);
          this.isLoading.set(false);
        }
      });
    });
  }

  ngOnInit(): void {
    // Model fetching logic is now safely handled by the reactive effect in the constructor.
  }

  /**
   * Fetches models directly assigned to the current project using a field-optimized payload.
   * Utilizes the centralized project data service to ensure the NgRx store receives the cached data correctly.
   * @param {string} projectId - The active project ID string.
   */
  private fetchModels(projectId: string): void {
    const cached = this.store.selectSignal(selectProjectModelsList)();
    if (cached && cached.length > 0) {
      this.models.set(cached);
      this.isLoading.set(false);
      if (Object.keys(this.expandedGroups()).length === 0) {
        const initialExpandedState: Record<string, boolean> = {};
        this.groupedModels().forEach(
          (g: ProjectModelGroup) => (initialExpandedState[g.domainKey] = true),
        );
        this.expandedGroups.set(initialExpandedState);
      }
    } else {
      this.isLoading.set(true);
    }

    this.projectDataService
      .fetchAndCacheLimitedProjectModelsList(projectId)
      .subscribe({
        next: (res: any[]) => {
          this.models.set(res);

          const initialExpandedState: Record<string, boolean> = {
            ...this.expandedGroups(),
          };
          this.groupedModels().forEach((g: ProjectModelGroup) => {
            if (initialExpandedState[g.domainKey] === undefined) {
              initialExpandedState[g.domainKey] = true;
            }
          });
          this.expandedGroups.set(initialExpandedState);

          this.isLoading.set(false);
        },
        error: () => {
          if (this.models().length === 0) {
            this.notificationService.createNotification(
              'error',
              'Error',
              this.config.notifications.fetchFailed,
            );
          }
          this.isLoading.set(false);
        },
      });
  }

  /**
   * Toggles the collapsed/expanded state of a specific domain group.

  /**
   * Toggles the collapsed/expanded state of a specific domain group.
   * @param {string} domainKey - Target domain key identifier.
   */
  public toggleGroup(domainKey: string): void {
    this.expandedGroups.update((state) => toggleRecordState(state, domainKey));
  }

  /**
   * Toggles all visible domain groups between fully expanded and fully collapsed.
   */
  public toggleAllGroups(): void {
    const targetState = !this.areAllGroupsExpanded();
    this.expandedGroups.update((state) =>
      setAllRecordStates(state, targetState),
    );
  }

  /**
   * Updates the selected domain filter. Null indicates "All Domains".
   * @param {string | null} domainKey - The key of the domain to filter by.
   */
  public selectDomain(domainKey: string | null): void {
    this.selectedDomain.set(domainKey);
  }

  /**
   * Router bridge triggering navigation into detailed table inspection.
   * @param {string} modelId - Target model string identifier.
   */
  public goToDetails(modelId: string): void {
    this.router.navigate(['/database', modelId]);
  }

  /**
   * Opens the AI Context Modal and fetches the structured schemas strictly for the displayed models.
   * Leverages the SDK to securely fetch the compiled data context string natively.
   */
  public openAiContextModal(): void {
    this.isAiContextModalVisible.set(true);
    this.isAiContextLoading.set(true);
    this.aiContextContent.set('');
    this.isCopied.set(false);

    // Utilize pure function to determine exact model IDs needed based on current view filters
    const displayedIds = extractDisplayedModelIds(
      this.searchQuery(),
      this.selectedDomain(),
      this.filteredGroupedModels(),
    );

    this.projectModelController
      .getDescriptionsForAiContext({ projectModelIds: displayedIds })
      .subscribe({
        next: (context: string) => {
          this.aiContextContent.set(context);
          this.isAiContextLoading.set(false);
        },
        error: () => {
          this.notificationService.createNotification(
            'error',
            'Error',
            this.config.notifications.fetchFailed,
          );
          this.isAiContextLoading.set(false);
          this.isAiContextModalVisible.set(false);
        },
      });
  }

  /**
   * Natively closes the AI Context Modal.
   */
  public closeAiContextModal(): void {
    this.isAiContextModalVisible.set(false);
  }

  /**
   * Copies the retrieved AI Context text directly to the user's system clipboard using standard browser APIs.
   * Momentarily toggles a visual feedback signal.
   */
  public async copyAiContextToClipboard(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.aiContextContent());
      this.isCopied.set(true);
      setTimeout(() => this.isCopied.set(false), 2000);
    } catch (err) {
      this.notificationService.createNotification(
        'error',
        'Error',
        'Failed to copy to clipboard. Your browser might block clipboard access.',
      );
    }
  }

  /**
   * Opens the cascade delete approval modal for a single table.
   */
  public openDeleteModelModal(model: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.isFreeTier()) {
      this.notificationService.createNotification(
        'warning',
        'Schema Action Restricted',
        'Free tier workspaces cannot delete tables. Upgrade to a paid plan to manage custom schema deletions.',
      );
      return;
    }
    this.deleteTargetType.set('model');
    this.modelToDelete.set(model);
    this.groupToDelete.set(null);
    this.deleteConfirmationInput.set('');
    this.isDeleting.set(false);
    this.isDeleteModalVisible.set(true);
  }

  /**
   * Opens the cascade delete approval modal for an entire domain group.
   */
  public openDeleteGroupModal(group: ProjectModelGroup, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.isFreeTier()) {
      this.notificationService.createNotification(
        'warning',
        'Schema Action Restricted',
        'Free tier workspaces cannot delete databases. Upgrade to a paid plan to manage custom schema deletions.',
      );
      return;
    }
    this.deleteTargetType.set('group');
    this.groupToDelete.set(group);
    this.modelToDelete.set(null);
    this.deleteConfirmationInput.set('');
    this.isDeleting.set(false);
    this.isDeleteModalVisible.set(true);
  }

  /**
   * Closes the cascade deletion modal and resets input state.
   */
  public closeDeleteModal(): void {
    if (this.isDeleting()) return;
    this.isDeleteModalVisible.set(false);
    this.modelToDelete.set(null);
    this.groupToDelete.set(null);
    this.deleteConfirmationInput.set('');
  }

  /**
   * Executes the cascade deletion once approval confirmation is validated.
   */
  public confirmCascadeDelete(): void {
    if (this.isFreeTier()) {
      this.closeDeleteModal();
      return;
    }
    if (!this.isDeleteConfirmed() || this.isDeleting()) {
      return;
    }
    this.isDeleting.set(true);

    if (this.deleteTargetType() === 'model') {
      const model = this.modelToDelete();
      if (!model?.id) {
        this.isDeleting.set(false);
        return;
      }
      const modelId = model.id;
      const modelDisplayName = model.title || model.name;

      this.projectModelController.deleteById({ id: modelId }).subscribe({
        next: () => {
          this.models.update((prev) => prev.filter((m) => m.id !== modelId));
          this.isDeleting.set(false);
          this.closeDeleteModal();
          this.notificationService.createNotification(
            'success',
            'Success',
            `Table "${modelDisplayName}" and all associated endpoints, relations, and schemas have been deleted.`,
          );
        },
        error: (err: any) => {
          this.isDeleting.set(false);
          const errMsg =
            err?.error?.error?.message ||
            err?.message ||
            'Failed to delete table.';
          this.notificationService.createNotification('error', 'Error', errMsg);
        },
      });
    } else {
      const group = this.groupToDelete();
      if (!group) {
        this.isDeleting.set(false);
        return;
      }
      const modelIds = group.models.map((m) => m.id).filter((id) => !!id);
      if (modelIds.length === 0) {
        this.isDeleting.set(false);
        this.closeDeleteModal();
        return;
      }

      const deletes$ = modelIds.map((id) =>
        this.projectModelController.deleteById({ id }),
      );

      forkJoin(deletes$).subscribe({
        next: () => {
          const idSet = new Set(modelIds);
          this.models.update((prev) => prev.filter((m) => !idSet.has(m.id)));
          this.isDeleting.set(false);
          this.closeDeleteModal();
          this.notificationService.createNotification(
            'success',
            'Success',
            `Group "${group.domainName}" and all ${modelIds.length} tables, endpoints, and schemas have been deleted.`,
          );
        },
        error: (err: any) => {
          this.isDeleting.set(false);
          const errMsg =
            err?.error?.error?.message ||
            err?.message ||
            'Failed to delete group.';
          this.notificationService.createNotification('error', 'Error', errMsg);
        },
      });
    }
  }

  /**
   * Legacy wrapper for single model deletion.
   */
  public deleteModel(model: any, event?: Event): void {
    this.openDeleteModelModal(model, event);
  }

  /**
   * Legacy wrapper for group deletion.
   */
  public deleteGroup(group: ProjectModelGroup, event?: Event): void {
    this.openDeleteGroupModal(group, event);
  }
}
